import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "../lib/scroll";
import { checkOwner, getMe } from "../lib/auth";

const LINKS = [
  { label: "The Work", href: "#work", id: "nav-link-work" },
  { label: "Packages", href: "#packages", id: "nav-link-packages" },
  { label: "Schedule", href: "#schedule", id: "nav-link-schedule" },
  { label: "About", href: "#about", id: "nav-link-about" },
  { label: "Book", href: "#booking", id: "nav-link-booking" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [auth, setAuth] = useState(null); // null = checking, else { mode: "anon" | "customer" | "owner" }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const owner = await checkOwner();
      if (cancelled) return;
      if (owner) {
        setAuth({ mode: "owner" });
        return;
      }
      const me = await getMe();
      if (cancelled) return;
      setAuth({ mode: me ? "customer" : "anon" });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  const account =
    auth === null
      ? null
      : auth.mode === "owner"
      ? { label: "My studio", href: "/inbox", testid: "nav-account-link" }
      : auth.mode === "customer"
      ? { label: "My games", href: "/my-games", testid: "nav-account-link" }
      : { label: "Sign in", href: "/signin", testid: "nav-signin-link" };

  const accountEl = (extra) =>
    account && (
      <a
        href={account.href}
        data-testid={account.testid}
        onClick={(e) => {
          e.preventDefault();
          setOpen(false);
          window.location.href = account.href;
        }}
        {...extra}
        className={`${extra?.className || ""} text-sm font-semibold text-white/70 transition-colors duration-200 hover:text-accentlight`}
      >
        {account.label}
      </a>
    );

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-ink/70 backdrop-blur-xl">
      <nav className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-6 md:h-20 md:px-10">
        <a
          href="#top"
          data-testid="nav-logo-link"
          onClick={(e) => go(e, "#top")}
          className="group flex items-center gap-3"
        >
          <span className="text-accentlight transition-transform duration-300 group-hover:rotate-6">
            <Logo />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-display text-2xl tracking-wider">
              GAME<span className="text-accentlight">LENS</span>
            </span>
            <span className="text-[9px] font-semibold uppercase tracking-[0.45em] text-white/50">
              Photography
            </span>
          </span>
        </a>

        <div className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              onClick={(e) => go(e, l.href)}
              className="text-sm font-semibold text-white/70 transition-colors duration-200 hover:text-accentlight"
            >
              {l.label}
            </a>
          ))}
          {accountEl({ "data-slot": "desktop" })}
          <a
            href="#booking"
            data-testid="nav-cta-button"
            onClick={(e) => go(e, "#booking")}
            className="rounded-full bg-gradient-to-r from-accent to-accentlight px-5 py-2.5 text-sm font-bold text-ink transition-all duration-300 hover:scale-[1.04] hover:brightness-110"
          >
            Book a game
          </a>
        </div>

        <button
          className="rounded-md p-2 text-white/80 transition-colors hover:text-accentlight md:hidden"
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div
          className="flex flex-col gap-1 border-t border-white/5 bg-ink/95 px-6 py-4 backdrop-blur-xl md:hidden"
          data-testid="nav-mobile-menu"
        >
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              onClick={(e) => go(e, l.href)}
              className="rounded-lg px-3 py-3 font-display text-2xl tracking-wider text-white/80 transition-colors hover:bg-surface hover:text-accentlight"
            >
              {l.label}
            </a>
          ))}
          {accountEl({ "data-slot": "mobile", className: "rounded-lg px-3 py-3 font-display text-2xl tracking-wider text-white/80 hover:bg-surface hover:text-accentlight" })}
          <a
            href="#booking"
            data-testid="nav-mobile-cta-button"
            onClick={(e) => go(e, "#booking")}
            className="mt-2 rounded-full bg-gradient-to-r from-accent to-accentlight px-5 py-3 text-center text-sm font-bold text-ink"
          >
            Book a game
          </a>
        </div>
      )}
    </header>
  );
}